/*******************************************************************
* PAGE        : EventList
* REMARKS     :
* CREATED ON  : 26-08-2026
* AUTHOR      : Bamroliya.Paras
* MODIFY      : Bamroliya.Paras
* MODIFY ON   : 26-08-2026
********************************************************************/

import { TablePagination, Tooltip } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import addsquare from "../../assets/add-square.svg";
import editsquare from "../../assets/edit-square.svg";
import filtericon from "../../assets/filter-icon.svg";
import promocode from "../../assets/promo-code.svg";
import ticketicon from "../../assets/ticket-icon.svg";
import LoaderComponent from '../../components/LoaderComponent/LoaderComponent';
import NotificationComponent from '../../components/NotificationComponent/NotificationComponent';
import { deleteEvent, getEventList } from '../../services/apiController';
import ButtonComponent from '../../components/ButtonComponent/ButtonComponent';

// #region Component
const EventList = () => {

    // #region States
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);

    const [eventsList, setEventsData] = useState([]);
    // State to manage snackbar visibility and messages
    const [snackbarStates, setSnackbarStates] = useState({ isSnackbarOpen: false, snackbarMessage: '' });
    const handleCloseSnackbar = () => {
        setSnackbarStates({ isSnackbarOpen: false, snackbarMessage: '', snackbarSeverity: '' });
    };

    const [rowsPerPage, setRowsPerPage] = useState(10); // Default rows per page
    const [currentPage, setCurrentPage] = useState(1); // Default current page
    const [maxRows, setMaxRows] = useState(0); // Total number of rows
    const [gotoPage, setGotoPage] = useState(1); // Page input for jump
    const [totalPages, setTotalPages] = useState(1); // Total pages calculation
    const [searchQuery, setSearchQuery] = useState(''); // For search query
    const [sortOrder, setSortOrder] = useState({ column: '', order: 'asc' }); // For sorting state
    const [columnCount, setColumnCount] = useState(1);

    // #endregion
    useEffect(() => {
        const thCount = document.querySelectorAll('thead th').length;
        setColumnCount(thCount || 1);
    }, []);

    useEffect(() => {
        wsGetEventList();
    }, [rowsPerPage, currentPage]); // Re-run the effect when rowsPerPage or currentPage changes

    const wsGetEventList = async () => {
        setLoading(true);
        getEventList({}).then((response) => {
            if (response) {
                setEventsData(response.tickets);
                setMaxRows(response?.tickets?.length); // Set total rows from response
                setTotalPages(Math.ceil(response?.tickets?.length / rowsPerPage)); // Calculate total pages
            } else {
                // Handle error scenario
            }
            setLoading(false);
        }).catch((error) => {
            setLoading(false);
            // Handle API error
        });
    }

    // #region Methods
    // Handle page change
    const handleChangePage = (event, newPage) => {
        setCurrentPage(newPage + 1); // MUI TablePagination uses 0-based index
    };

    // Handle rows per page change
    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setCurrentPage(1); // Reset to page 1 when rows per page changes
    };

    // Handle page input change
    const handleGotoPageChange = (event) => {
        setGotoPage(event.target.value);
    };

    // Handle the submit (Enter key press) for "Go to page"
    const handleSubmitGotoPage = (event) => {
        if (event.key === 'Enter') {
            const pageNumber = Math.max(1, Math.min(totalPages, parseInt(gotoPage, 10))); // Ensure valid page
            setCurrentPage(pageNumber);
        }
    };

    // Handle search query change
    const handleSearchChange = (event) => {
        setSearchQuery(event.target.value);
    };

    // Filtered and sorted eventsList
    const SEARCH_FIELDS = [
        'EventName',
        'EventDate',
        'EventDay',
        'EventTime',
        'Venue',
        'Country',
        'CountryCode',
        'Currency',
        'EnquiryToEmailId',
        'BCCEmailId',
        'MaxLimit',
        'EventPostpone',
        'EventClose',
    ];

    const filteredEvents = eventsList?.filter((itemEvent) =>
        SEARCH_FIELDS.some((field) =>
            itemEvent[field]
                ?.toString()
                .toLowerCase()
                .includes(searchQuery.toLowerCase())
        )
    );


    // Sorting function
    const handleSort = (column) => {
        const newOrder = sortOrder.order === 'asc' ? 'desc' : 'asc';
        setSortOrder({
            column: column,
            order: newOrder,
        });
    };

    const sortedEvents = filteredEvents.sort((a, b) => {
        if (!sortOrder.column) return 0;

        const aValue = a[sortOrder.column];
        const bValue = b[sortOrder.column];

        if (aValue < bValue) return sortOrder.order === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortOrder.order === 'asc' ? 1 : -1;
        return 0;
    });

    const wsDeleteEvent = async (ticketMasterId) => {
        deleteEvent({ ticket_master_id: ticketMasterId })
            .then((response) => {
                if (response) {
                    if (response.status === 1) {
                        wsGetEventList();
                        setSnackbarStates({ isSnackbarOpen: true, snackbarMessage: response.message || "Event deleted successfully", snackbarSeverity: "success" });
                    } else {
                        setSnackbarStates({ isSnackbarOpen: true, snackbarMessage: response?.message || "Event delete failed", snackbarSeverity: "error" });
                    }
                }
                setLoading(false);
            }).catch((error) => {
                setLoading(false);
                // Handle API error
            });
    }

    const formatEventDate = (eventDate) => {

        const formattedDate = new Date(eventDate)
            .toLocaleDateString('en-GB', {
                day: '2-digit',
                month: 'short',
                year: 'numeric'
            });

        return formattedDate;
    }

    // #endregion

    return (loading ? <LoaderComponent />
        : <React.Fragment>
            <NotificationComponent isOpen={snackbarStates.isSnackbarOpen} message={snackbarStates.snackbarMessage} onClose={handleCloseSnackbar} severity={snackbarStates.snackbarSeverity} />

            <div style={{ padding: '10px 20px 50px', overflowX: 'auto' }}>
                <div className='table-heading-search' style={{ marginBottom: '10px', }}>
                    <h2 className='dialog-heading' style={{ marginBlockEnd: '0px' }}>Event List</h2>

                    {/* Search Input */}
                    <div className='table-heading-search' style={{ alignItems: 'center', gap: '16px' }}>
                        Search: <input type="text" value={searchQuery} onChange={handleSearchChange} style={{ padding: '5px', width: '200px', border: '1px solid #ccc', borderRadius: '4px', }} />
                        <ButtonComponent style={{ padding: "8px 16px", textTransform: 'none' }} onClick={() => navigate('/about-event')}>Add New Event</ButtonComponent>
                    </div>

                </div>

                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                        <tr>
                            <th className='tableHeaderStyle' onClick={() => handleSort('EventName')}>Event Name</th>
                            <th className='tableHeaderStyle' onClick={() => handleSort('EventDate')}>Event Date / Time</th>
                            <th className='tableHeaderStyle' onClick={() => handleSort('EventDay')}>Event Day</th>
                            {/* <th className='tableHeaderStyle' onClick={() => handleSort('EventTime')}>E-Mail</th> */}
                            <th className='tableHeaderStyle' onClick={() => handleSort('Venue')}>Venue</th>
                            <th className='tableHeaderStyle' onClick={() => handleSort('Country')}>Country</th>
                            {/* <th className='tableHeaderStyle' onClick={() => handleSort('CountryCode')}>Country Code</th> */}
                            <th className='tableHeaderStyle' onClick={() => handleSort('Currency')}>Currency</th>
                            <th className='tableHeaderStyle' onClick={() => handleSort('EnquiryToEmailId')}>Enquiry To Email</th>
                            <th className='tableHeaderStyle' onClick={() => handleSort('BCCEmailId')}>BCC-Email</th>
                            <th className='tableHeaderStyle' onClick={() => handleSort('MaxLimit')} style={{ textAlign: 'right' }}>Max Limit</th>
                            <th className='tableHeaderStyle' onClick={() => handleSort('EventPostpone')} style={{ textAlign: 'right' }}>Event Postpone ?</th>
                            <th className='tableHeaderStyle' onClick={() => handleSort('EventClose')} style={{ textAlign: 'right' }}>Event Close ?</th>
                            <th className='tableHeaderStyle' style={{ textAlign: 'center' }}><img src={filtericon} alt='filtericon' /></th>
                        </tr>
                    </thead>
                    <tbody>
                        {!sortedEvents.length
                            ? <tr>
                                <td colSpan={columnCount} style={{ textAlign: 'center', padding: '10px', fontSize: '16px' }}>
                                    No Record Found ☹️
                                </td>
                            </tr>
                            : sortedEvents.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage)
                                .map((eventItem, index) => (
                                    <tr key={index} style={{ ...tableRowStyle, backgroundColor: index % 2 === 1 ? '#ececec' : '#FFFFFF' }}>
                                        <td className='tableCellStyle'>{eventItem.EventName}</td>
                                        <td className='tableCellStyle'>{formatEventDate(eventItem.EventDate) + " " + eventItem.EventTime}</td>
                                        <td className='tableCellStyle'>{eventItem.EventDay}</td>
                                        {/* <td className='tableCellStyle'>{eventItem.EventTime}</td> */}
                                        <td className='tableCellStyle'>{eventItem.Venue}</td>
                                        <td className='tableCellStyle'>{eventItem.Country + " " + eventItem.CountryCode}</td>
                                        {/* <td className='tableCellStyle'>{eventItem.CountryCode}</td> */}
                                        <td className='tableCellStyle'>{eventItem.Currency}</td>
                                        <td className='tableCellStyle'>{eventItem.EnquiryToEmailId}</td>
                                        <td className='tableCellStyle'>{eventItem.BCCEmailId}</td>
                                        <td className='tableCellStyle' style={{ textAlign: 'right' }}>{eventItem.MaxLimit}</td>
                                        <td className='tableCellStyle' style={{ textAlign: 'right' }}>{eventItem.EventPostpone}</td>
                                        <td className='tableCellStyle' style={{ textAlign: 'right' }}>{eventItem.EventClose}</td>
                                        <td className='tableCellStyle' style={{ display: 'flex', gap: '8px' }}>

                                            <Tooltip title="Event Promocode" arrow>
                                                <img src={promocode} alt='promocode' style={{ cursor: 'pointer' }} onClick={() => navigate('/event-promocode', { state: { eventMasterId: eventItem.TicketMasterId } })} />
                                            </Tooltip>

                                            <Tooltip title="Ticket Rate" arrow>
                                                <img src={ticketicon} alt='ticketicon' style={{ cursor: 'pointer' }} onClick={() => navigate('/event-ticketrate', { state: { eventMasterId: eventItem.TicketMasterId } })} />
                                            </Tooltip>

                                            <Tooltip title="Edit Event" arrow>
                                                <img src={editsquare} alt='editsquare' style={{ cursor: 'pointer' }} onClick={() => navigate('/about-event', { state: { eventMasterId: eventItem.TicketMasterId } })} />
                                            </Tooltip>

                                            {/* <img src={deleteicon} alt='deleteicon' style={{ cursor: 'pointer' }} onClick={() => wsDeleteEvent(eventItem.TicketMasterId)} /> */}
                                        </td>
                                    </tr>
                                ))}
                    </tbody>
                </table>

                {/* Pagination Controls */}
                <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', padding: '10px' }}>
                    <TablePagination
                        rowsPerPageOptions={[10, 25, 50, 100, 200, 500, 1000]}
                        component="div"
                        count={maxRows}
                        rowsPerPage={rowsPerPage}
                        page={currentPage - 1} // MUI TablePagination uses 0-based index
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        id="page-box"
                    />
                    <div style={{ fontSize: '13px', marginLeft: '10px' }}>
                        Page: <input style={{ width: '50px', border: '1px solid #ccc', borderRadius: '5px', padding: '4px' }} type="number" value={gotoPage} disabled={totalPages <= 1} onChange={handleGotoPageChange} onKeyDown={handleSubmitGotoPage} /> Of {totalPages}
                    </div>
                </div>
            </div>
        </React.Fragment>
    );
};
// #endregion

const tableRowStyle = {
    borderBottom: '1px solid #eee',
};

export default EventList;